import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Container,
  Box,
  TextField,
  Button,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  CircularProgress,
  SvgIcon,
  Typography,
  Autocomplete,
  Stack,
} from "@mui/material";
import makeRequest from "app/api";
import BrowserNotSupportedIcon from "@mui/icons-material/BrowserNotSupported";

const DealEdit = () => {
  const params = useParams();
  const [deal, setDeal] = useState({ loading: true, data: {} });
  const [isValidDeal, setIsValidDeal] = useState(false);
  const [stages, setStages] = useState([]);
  const [stage, setStage] = useState("");
  const [dealType, setDealType] = useState("negotiation");
  const [vendor, setVendor] = useState({ name: "", value: null });
  const [customer, setCustomer] = useState({ name: "", value: null });
  const [carrier, setCarrier] = useState({ name: "", value: null });
  const [partners, setPartners] = useState({ loading: true, data: [] });

  // 1- Get the object that I'm going to modify
  async function getDealById() {
    try {
      const endpoint = "/reservaerp/restful/deals";
      const method = "POST";
      const data_ = { deal_id: params.id };
      const response = await makeRequest(method, endpoint, data_);
      const { data } = response.data.result;
      if (data.length !== 0) {
        setIsValidDeal(true);
        setDeal((prevState) => ({ ...prevState, data: data[0] }));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDeal((prevState) => ({ ...prevState, loading: false }));
    }
  }

  useEffect(() => {
    getDealById();
  }, []);

  useEffect(() => {
    if (Object.keys(deal.data).length !== 0) {
      setStage(deal.data.stage_id[0]);
      setDealType(deal.data.deal_type);
    }
  }, [deal.data]);

  // 2- Get the stages list
  async function fetchStages() {
    try {
      const endpoint = "/reservaerp/restful/deals/stages";
      const method = "POST";
      const data_ = { limit: 20 };
      const response = await makeRequest(method, endpoint, data_);
      const { data } = response.data.result;
      setStages(data);
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    fetchStages();
  }, []);

  // 3- Get the partners
  async function fetchPartners(offset) {
    try {
      const endpoint = "/reservaerp/restful/partners";
      const method = "GET";
      const data_ = { offset: offset, limit: 80 };
      const response = await makeRequest(method, endpoint, data_);

      const { data, count } = response.data.result;
      setPartners((prevPartners) => ({
        ...prevPartners,
        data: [...prevPartners.data, ...data],
        loading: data.length < 80 || offset + 80 >= count ? false : true,
      }));
      if (data.length < 80 || offset + 80 >= count) {
        // No more data to load
      } else {
        fetchPartners(offset + 80);
      }
    } catch (error) {
      console.error(error);
    }
  }

  useEffect(() => {
    fetchPartners(0);
  }, []);

  useEffect(() => {
    if (!partners.loading) {
      let defaultOption = partners.data.find(
        (option) => option.id === deal.data.vendor_id[0]
      );
      if (defaultOption) {
        setVendor({ name: defaultOption.name, value: defaultOption });
      }
      defaultOption = partners.data.find(
        (option) => option.id === deal.data.customer_id[0]
      );
      if (defaultOption) {
        setCustomer({ name: defaultOption.name, value: defaultOption });
      }
      if (deal.data.carrier_id) {
        defaultOption = partners.data.find(
          (option) => option.id === deal.data.carrier_id[0]
        );
        if (defaultOption) {
          setCarrier({ name: defaultOption.name, value: defaultOption });
        }
      }
    }
  }, [partners.data]);

  // Events
  const handleStageSelectChange = (event) => {
    setStage(event.target.value);
  };

  const handleDealTypeSelectChange = (event) => {
    setDealType(event.target.value);
  };

  const handleVendorInputChange = (event, value) => {
    setVendor((prevState) => ({ ...prevState, name: value }));
  };

  const handleVendorOptionSelect = (event, value) => {
    setVendor((prevState) => ({ ...prevState, value: value }));
  };

  const handleCustomerInputChange = (event, value) => {
    setCustomer((prevState) => ({ ...prevState, name: value }));
  };

  const handleCustomerOptionSelect = (event, value) => {
    setCustomer((prevState) => ({ ...prevState, value: value }));
  };

  const handleCarrierInputChange = (event, value) => {
    setCarrier((prevState) => ({ ...prevState, name: value }));
  };

  const handleCarrierOptionSelect = (event, value) => {
    setCarrier((prevState) => ({ ...prevState, value: value }));
  };

  async function updateDeal() {
    console.log(deal.data);
  }

  return (
    <Container
      sx={{
        py: 2,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {!deal.loading ? (
        isValidDeal ? (
          <Box
            component="form"
            noValidate
            sx={{
              display: "flex",
              width: "100%",
              flexDirection: "column",
              gap: 1,
              "& > :not(style)": { m: 1 },
            }}
          >
            <Typography variant="h6">{`Deal ${deal.data.name}`}</Typography>
            {/* Deal type and Stage */}
            <Stack direction="row" spacing={1}>
              <FormControl sx={{ width: "200px" }}>
                <InputLabel id="deal-type-select-label">Deal Type</InputLabel>
                <Select
                  labelId="deal-type-select-label"
                  id="deal-type-select"
                  value={dealType}
                  label="Deal Type"
                  onChange={handleDealTypeSelectChange}
                >
                  <MenuItem value="negotiation">Negotiation</MenuItem>
                  <MenuItem value="directship">Direct Ship</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ width: "200px" }}>
                <InputLabel id="stage-select-label">Stage</InputLabel>
                <Select
                  labelId="stage-select-label"
                  id="stage-select"
                  value={stage}
                  label="Stage"
                  onChange={handleStageSelectChange}
                >
                  {stages.map((stage_) => (
                    <MenuItem key={stage_.id} value={stage_.id}>
                      {stage_.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            {/* Vendor and Customer */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <Stack direction="column" spacing={1} sx={{ width: "50%" }}>
                <Autocomplete
                  fullWidth
                  options={!partners.loading ? partners.data : []}
                  getOptionLabel={(option) => option.name}
                  value={vendor.value}
                  loading={partners.loading}
                  onInputChange={handleVendorInputChange}
                  onChange={handleVendorOptionSelect}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box key={option.id} component="li" {...optionProps}>
                        {option.name}
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vendor"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {partners.loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                {vendor.value ? (
                  <Box px={1}>
                    <Typography>{vendor.value.street}</Typography>
                    <Typography>{vendor.value.street2}</Typography>
                    <Typography>{vendor.value.state_id[1]}</Typography>
                    <Typography>{vendor.value.country_id[1]}</Typography>
                  </Box>
                ) : (
                  <Box px={1}>
                    <Typography>-</Typography>
                  </Box>
                )}
              </Stack>
              <Stack direction="column" spacing={1} sx={{ width: "50%" }}>
                <Autocomplete
                  fullWidth
                  options={!partners.loading ? partners.data : []}
                  getOptionLabel={(option) => option.name}
                  value={customer.value}
                  loading={partners.loading}
                  onInputChange={handleCustomerInputChange}
                  onChange={handleCustomerOptionSelect}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box key={option.id} component="li" {...optionProps}>
                        {option.name}
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {partners.loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                {customer.value ? (
                  <Box px={1}>
                    <Typography>{customer.value.street}</Typography>
                    <Typography>{customer.value.street2}</Typography>
                    <Typography>{customer.value.state_id[1]}</Typography>
                    <Typography>{customer.value.country_id[1]}</Typography>
                  </Box>
                ) : (
                  <Box px={1}>
                    <Typography>-</Typography>
                  </Box>
                )}
              </Stack>
            </Box>
            {/* Carrier */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              <Stack direction="column" spacing={1} sx={{ width: "50%" }}>
                <Autocomplete
                  fullWidth
                  options={!partners.loading ? partners.data : []}
                  getOptionLabel={(option) => option.name}
                  value={carrier.value}
                  loading={partners.loading}
                  onInputChange={handleCarrierInputChange}
                  onChange={handleCarrierOptionSelect}
                  renderOption={(props, option) => {
                    const { key, ...optionProps } = props;
                    return (
                      <Box key={option.id} component="li" {...optionProps}>
                        {option.name}
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Carrier"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {partners.loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
                {carrier.value ? (
                  <Box px={1}>
                    <Typography>{carrier.value.street}</Typography>
                    <Typography>{carrier.value.street2}</Typography>
                    <Typography>{carrier.value.state_id[1]}</Typography>
                    <Typography>{carrier.value.country_id[1]}</Typography>
                  </Box>
                ) : (
                  <Box px={1}>
                    <Typography>-</Typography>
                  </Box>
                )}
              </Stack>
              <Stack
                direction="column"
                spacing={1}
                sx={{ width: "50%" }}
              ></Stack>
            </Box>
            {/* Dates */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
              {/* AQUIIIIIIIIIIII */}
              <Stack direction="row" spacing={1}>
                {/*
                                <DatePicker
                                    value={dealDate}
                                    onChange={handleDealDateChange}
                                    format="yyyy-MM-dd"
                                />
                                */}
              </Stack>
            </Box>
            <Button variant="contained" onClick={updateDeal}>
              Update Deal
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              py: 2,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <SvgIcon sx={{ fontSize: 200 }}>
              <BrowserNotSupportedIcon />
            </SvgIcon>
            <Typography variant="h6">Deal not found.</Typography>
          </Box>
        )
      ) : (
        <Box
          sx={{
            height: "90vh",
            width: "90vw",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}
    </Container>
  );
};

export default DealEdit;
